/**
 * AdminQueue (/admin/queue): the scan job queue, on mockup carbon/01's brief
 * and carbon/02's tables.
 *
 *   1. The brief (BriefHero): what to do about the queue (check an unhealthy
 *      processor, clear stuck jobs, or leave it to run), with "Clear stuck
 *      jobs" (the primary while any job is stuck) and "Emergency stop" beside
 *      it. Both confirm first in a ConfirmDialog; the stop is a danger
 *      confirm.
 *   2. Three tiles in a condensed grid: waiting, running, the processor.
 *   3. Running jobs and Queued jobs as DataTables, the auto-refresh controls
 *      in the first one's toolbar.
 */
import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import toast from 'react-hot-toast'
import { formatDistanceToNow } from 'date-fns'
import type { Tenant } from '@/types'
import type { ActionBrief } from '@/lib/brief-shape'
import { BigNumber } from '@/components/carbon/BigNumber'
import { BriefHero } from '@/components/carbon/BriefHero'
import { Button } from '@/components/carbon/Button'
import { Checkbox } from '@/components/carbon/Checkbox'
import { CondensedGrid } from '@/components/carbon/CondensedGrid'
import { ConfirmDialog } from '@/components/carbon/ConfirmDialog'
import { DataTable, TableItem, type DataColumn } from '@/components/carbon/DataTable'
import { InlineNotification } from '@/components/carbon/InlineNotification'
import { ProgressBar } from '@/components/carbon/ProgressBar'
import { TableSelect } from '@/components/carbon/TableSelect'
import { Tile } from '@/components/carbon/Tile'
import { TileHeader } from '@/components/carbon/TileHeader'
import { formatClock, formatDuration, jobTypeLabel, timeSpanToMs } from '@/components/AdminPortal/admin-format'
import { STUCK_AFTER_MS, composeQueueBrief } from '@/components/AdminPortal/page-briefs'

interface QueuedJob {
  jobId: string
  tenantId: string
  jobType: string
  status: string
  createdBy: string
  createdAt: string
  startedAt?: string
  progressPercentage: number
  currentStep?: string
  statusMessage?: string
  // A .NET TimeSpan string, like the systemHealth durations below.
  estimatedWaitTime: string | number | null
}

interface RunningJob {
  jobId: string
  tenantId: string
  jobType: string
  status: string
  createdBy: string
  startedAt?: string
  progressPercentage: number
  currentStep?: string
  statusMessage?: string
  runningDuration: number
}

interface DetailedQueueStatus {
  summary: {
    queueLength: number
    runningJobsCount: number
    totalActiveJobs: number
  }
  queuedJobs: QueuedJob[]
  runningJobs: RunningJob[]
  systemHealth: {
    isProcessorHealthy: boolean
    // .NET TimeSpans, sent as "[d.]hh:mm:ss[.fffffff]" strings (not milliseconds).
    // Typed loosely because the read is defensive: a number is taken as ms, anything else as unknown.
    longestQueuedJob: string | number | null
    averageJobDuration: string | number | null
  }
  timestamp: string
}

const INTERVALS = [
  { value: '2000', label: 'Every 2 seconds' },
  { value: '5000', label: 'Every 5 seconds' },
  { value: '10000', label: 'Every 10 seconds' },
  { value: '30000', label: 'Every 30 seconds' },
]

const LOAD_FAILED_BRIEF: ActionBrief = {
  headline: 'Reload the page to see the queue.',
  lede: '**The queue status did not load**, so Guardian cannot show what is running.',
}

export default function AdminQueue() {
  const [autoRefresh, setAutoRefresh] = useState(true)
  const [refreshInterval, setRefreshInterval] = useState(10000) // 10 seconds (reduced from 5s for better performance)
  const [confirming, setConfirming] = useState<null | 'stop' | 'clear'>(null)
  const [confirmBusy, setConfirmBusy] = useState(false)

  const { data: queueStatus, error, refetch, isLoading } = useQuery<DetailedQueueStatus>({
    queryKey: ['admin-queue-status'],
    queryFn: async () => {
      // The shared api type declares the systemHealth TimeSpans as numbers; the wire sends strings (see above).
      return (await api.getDetailedQueueStatus()) as unknown as DetailedQueueStatus
    },
    refetchInterval: autoRefresh ? refreshInterval : false,
    refetchIntervalInBackground: true
  })

  // Fetch tenants for domain name lookup
  const { data: tenants } = useQuery<Tenant[]>({
    queryKey: ['tenants'],
    queryFn: () => api.getTenants(),
    staleTime: 5 * 60 * 1000 // Cache for 5 minutes
  })

  // Create a lookup map from tenantId to domain name
  const tenantDomainMap = useMemo(() => {
    const map = new Map<string, string>()
    tenants?.forEach(tenant => {
      map.set(tenant.tenantId, tenant.domain || tenant.name)
    })
    return map
  }, [tenants])

  // Helper function to get domain name from tenantId
  const getTenantDomain = (tenantId: string) => {
    return tenantDomainMap.get(tenantId) || tenantId
  }

  // Both actions confirm first (ConfirmDialog); the confirm runs the request.
  const handleEmergencyStop = () => setConfirming('stop')
  const handleClearStuckJobs = () => setConfirming('clear')

  const confirmEmergencyStop = async () => {
    setConfirmBusy(true)
    try {
      await api.emergencyStop()
      toast.success('Emergency stop initiated')
      refetch()
    } catch (error) {
      toast.error('Failed to initiate emergency stop')
    } finally {
      setConfirmBusy(false)
      setConfirming(null)
    }
  }

  const confirmClearStuckJobs = async () => {
    setConfirmBusy(true)
    try {
      const data = await api.clearStuckJobs()
      toast.success(data.message || 'Stuck jobs cleared')
      refetch()
    } catch (error) {
      toast.error('Failed to clear stuck jobs')
    } finally {
      setConfirmBusy(false)
      setConfirming(null)
    }
  }

  const running = queueStatus?.runningJobs ?? []
  const queued = queueStatus?.queuedJobs ?? []
  const stuck = running.filter(job => job.runningDuration > STUCK_AFTER_MS).length

  const brief: ActionBrief = error
    ? LOAD_FAILED_BRIEF
    : composeQueueBrief({
        queued: queueStatus?.summary.queueLength ?? queued.length,
        running: queueStatus?.summary.runningJobsCount ?? running.length,
        stuck,
        healthy: queueStatus?.systemHealth.isProcessorHealthy ?? true,
      })

  const provenance = queueStatus
    ? `Checked by Guardian at ${formatClock(new Date(queueStatus.timestamp))} · ${
        autoRefresh ? `refreshes every ${refreshInterval / 1000} seconds` : 'auto-refresh is off'
      }`
    : undefined

  const actions = (
    <>
      <Button kind={stuck > 0 ? 'primary' : 'tertiary'} onClick={handleClearStuckJobs}>
        Clear stuck jobs
      </Button>
      <Button kind="tertiary" onClick={handleEmergencyStop}>
        Emergency stop
      </Button>
    </>
  )

  const jobCell = (job: { jobType: string; jobId: string }) => (
    <TableItem title={jobTypeLabel(job.jobType)} sub={<span className="font-mono">{job.jobId.substring(0, 8)}…</span>} />
  )
  const tenantCell = (job: { tenantId: string }) => <span title={job.tenantId}>{getTenantDomain(job.tenantId)}</span>

  const runningColumns: DataColumn<RunningJob>[] = [
    { id: 'job', header: 'Job', primary: true, cell: jobCell },
    { id: 'tenant', header: 'Tenant', inlineOnPhone: true, cell: tenantCell },
    {
      id: 'progress',
      header: 'Progress',
      cell: job => (
        <div className="flex items-center gap-2">
          <ProgressBar value={job.progressPercentage} max={100} label={`${job.progressPercentage}% done`} hideLabel className="w-24 flex-none" />
          <span className="text-helper tabular-nums text-text-secondary">{job.progressPercentage}%</span>
        </div>
      ),
    },
    { id: 'duration', header: 'Duration', nowrap: true, cell: job => formatDuration(job.runningDuration) },
    { id: 'step', header: 'Step', inlineOnPhone: true, cell: job => job.currentStep || 'Initializing' },
  ]

  const queuedColumns: DataColumn<QueuedJob>[] = [
    { id: 'job', header: 'Job', primary: true, cell: jobCell },
    { id: 'tenant', header: 'Tenant', inlineOnPhone: true, cell: tenantCell },
    {
      id: 'created',
      header: 'Created',
      inlineOnPhone: true,
      cell: job => formatDistanceToNow(new Date(job.createdAt), { addSuffix: true }),
    },
    {
      id: 'wait',
      header: 'Est. wait',
      nowrap: true,
      cell: job => {
        const waitMs = timeSpanToMs(job.estimatedWaitTime)
        return waitMs === null ? 'Not reported' : formatDuration(waitMs)
      },
    },
    { id: 'createdBy', header: 'Created by', inlineOnPhone: true, cell: job => job.createdBy },
  ]

  const health = queueStatus?.systemHealth
  const longestWaitMs = health ? timeSpanToMs(health.longestQueuedJob) : null
  const averageJobMs = health ? timeSpanToMs(health.averageJobDuration) : null
  const waitingCount = queueStatus?.summary.queueLength
  const waitingNote =
    typeof waitingCount !== 'number'
      ? 'Waiting for the queue count.'
      : waitingCount === 0
        ? 'Nothing is waiting.'
        : longestWaitMs !== null
          ? `The longest has waited ${formatDuration(longestWaitMs)}.`
          : `${waitingCount} ${waitingCount === 1 ? 'job is' : 'jobs are'} waiting.`
  const averageNote =
    averageJobMs === null
      ? 'The average is not reported.'
      : averageJobMs > 0
        ? `A job takes ${formatDuration(averageJobMs)} on average.`
        : 'No average yet.'

  return (
    <div>
      <section aria-labelledby="queue-verdict" className="mt-8">
        <BriefHero
          brief={brief}
          size="pillar"
          headingId="queue-verdict"
          isLoading={isLoading}
          provenance={provenance}
          secondaryAction={
            error ? (
              <Button kind="tertiary" onClick={() => refetch()}>
                Retry
              </Button>
            ) : (
              actions
            )
          }
        />
      </section>

      {error ? (
        <InlineNotification kind="error" title="Queue status did not load." className="mt-12">
          {error.message}
        </InlineNotification>
      ) : (
        <>
          <CondensedGrid as="section" columns={{ base: 1, 'c-md': 3 }} className="mt-8" aria-label="Queue at a glance">
            <Tile>
              <TileHeader title="Waiting" level={2} />
              <BigNumber value={isLoading ? null : queueStatus?.summary.queueLength ?? 0} size="figure-lg" className="mt-4" />
              <p className="m-0 mt-2 text-helper text-text-helper">
                {waitingNote}
              </p>
            </Tile>
            <Tile>
              <TileHeader title="Running" level={2} />
              <BigNumber value={isLoading ? null : queueStatus?.summary.runningJobsCount ?? 0} size="figure-lg" className="mt-4" />
              <p className="m-0 mt-2 text-helper text-text-helper">
                {averageNote}
              </p>
            </Tile>
            <Tile>
              <TileHeader title="Processor" level={2} />
              <p className="m-0 mt-4 text-figure font-light text-text-primary">
                {!health ? '—' : health.isProcessorHealthy ? 'Healthy' : 'Not healthy'}
              </p>
              <p className="m-0 mt-2 text-helper text-text-helper">
                {!health
                  ? 'Checking the processor.'
                  : health.isProcessorHealthy
                    ? 'It is picking up queued jobs.'
                    : 'It is not picking up queued jobs.'}
              </p>
            </Tile>
          </CondensedGrid>

          <DataTable
            className="mt-12"
            title="Running jobs"
            description="Scans the processor is working on now."
            columns={runningColumns}
            rows={running}
            rowId={job => job.jobId}
            rowLabel={job => `${jobTypeLabel(job.jobType)} ${job.jobId.substring(0, 8)}`}
            isLoading={isLoading}
            loadingRows={3}
            empty="No scan is running."
            toolbar={
              <>
                <div className="flex min-h-12 flex-1 items-center justify-end px-4 max-c-md:basis-full max-c-md:justify-start">
                  <Checkbox label="Auto-refresh" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} />
                </div>
                <TableSelect
                  label="Refresh"
                  value={String(refreshInterval)}
                  onChange={(value) => setRefreshInterval(Number(value))}
                  disabled={!autoRefresh}
                  options={INTERVALS}
                  className="c-md:min-w-[16rem]"
                />
              </>
            }
          />

          <DataTable
            className="mt-12"
            title="Queued jobs"
            description="Scans waiting for the processor."
            columns={queuedColumns}
            rows={queued}
            rowId={job => job.jobId}
            rowLabel={job => `${jobTypeLabel(job.jobType)} ${job.jobId.substring(0, 8)}`}
            isLoading={isLoading}
            loadingRows={3}
            empty={waitingCount ? `${waitingCount} ${waitingCount === 1 ? 'job is' : 'jobs are'} waiting; the list did not include them.` : 'Nothing is waiting.'}
          />
        </>
      )}

      <ConfirmDialog
        open={confirming === 'clear'}
        title="Clear the stuck jobs?"
        body="Every job running for over an hour is marked as failed and leaves the queue."
        confirmLabel="Clear stuck jobs"
        loading={confirmBusy}
        onConfirm={confirmClearStuckJobs}
        onCancel={() => setConfirming(null)}
      />
      <ConfirmDialog
        open={confirming === 'stop'}
        danger
        title="Stop the queue?"
        body="Every queued job is cancelled. Running jobs finish on their own."
        confirmLabel="Emergency stop"
        loading={confirmBusy}
        onConfirm={confirmEmergencyStop}
        onCancel={() => setConfirming(null)}
      />
    </div>
  )
}
